import { DownOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useTolgee } from '@tolgee/react';
import { Dropdown, Select } from 'antd';
import mixpanel from 'mixpanel-browser';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import theme from '../assets/theme';
import { SelectProps } from '../components/_atoms/_Select';
import useAccessBadge from '../hooks/useAccessBadge';
import { useBreakPoint } from '../hooks/useBreakPoint';
import { getBrowserLanguage, storePreferredLocale } from '../hooks/useLocale';
import { localeOptions } from '../providers/tolgee';
import backendService from '../services/backendService';

type GroupedOption =
  | { label: string; value: string }
  | { label: string; options: { label: string; value: string }[] };

const groupedOptions: GroupedOption[] = Object.values(
  localeOptions.reduce((acc, [value, displayName]) => {
    const key = value.slice(0, 2);
    if (!acc[key]) {
      acc[key] = { label: displayName, value };
    } else if ('options' in acc[key]) {
      (acc[key] as { options: { label: string; value: string }[] }).options.push({
        label: displayName,
        value,
      });
    } else {
      acc[key] = {
        label: displayName.slice(4), // hopefully all future flag emojis are 4 characters
        options: [
          acc[key] as { label: string; value: string },
          { label: displayName, value },
        ],
      };
    }
    return acc;
  }, {} as Record<string, { label: string; value: string } | { label: string; options: { label: string; value: string }[] }>),
);

const MobileSelector = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  background: transparent;
  border-radius: 6px;
  gap: 6px;
  min-width: 44px;
  justify-content: center;

  .flag {
    font-size: 20px;
  }

  .chevron {
    font-size: 10px;
    color: ${theme.colors.black};
    transition: transform 0.2s ease;
  }

  .chevron.rotated {
    transform: rotate(180deg);
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

export const LangSelector: React.FC<{
  selectProps?: Pick<SelectProps, 'label' | 'labelStyle' | 'size'>;
  hideMobileVersion?: boolean;
  onSelect?: (locale: string) => void;
}> = ({ selectProps, hideMobileVersion = false, onSelect }) => {
  const tolgee = useTolgee();
  const isMobile = !useBreakPoint(550);
  const { accessBadge, isFetching, refetchAccessBadge } = useAccessBadge();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: ({ locale }: { locale: string }) =>
      backendService.instance.users.editProfile({
        preferredLocale: locale,
      }),

    onSuccess: (result, { locale }) => {
      refetchAccessBadge();
      storePreferredLocale(result.preferredLocale ?? locale);
    }
  });

  const onChange = useCallback(
    (locale: string) => {
      if (accessBadge?.user?.id) {
        mutate({ locale }, { onSuccess: () => onSelect?.(locale) });
        storePreferredLocale(locale);
        tolgee.changeLanguage(locale);
      } else {
        onSelect?.(locale);
        storePreferredLocale(locale);
        tolgee.changeLanguage(locale);
      }
      mixpanel.track('locale changed', {
        selectedLocale: locale,
        detectedLocale: getBrowserLanguage(),
      });
    },
    [accessBadge, mutate, tolgee, onSelect],
  );

  const currentLanguage = tolgee.getLanguage();
  const currentLanguageDisplay = localeOptions.find(
    ([value]) => value === currentLanguage,
  )?.[1];

  const createDropdownItems = useMemo(
    () =>
      groupedOptions.map(option => {
        if ('options' in option) {
          return {
            key: option.label,
            label: option.label,
            type: 'group' as const,
            children: option.options.map(subOption => ({
              key: subOption.value,
              label: subOption.label,
              onClick: () => {
                onChange(subOption.value);
                setIsDropdownOpen(false);
              },
            })),
          };
        } else {
          return {
            key: option.value,
            label: option.label,
            onClick: () => {
              onChange(option.value);
              setIsDropdownOpen(false);
            },
          };
        }
      }),
    [groupedOptions, onChange],
  );

  if (isMobile && !hideMobileVersion) {
    const flag = currentLanguageDisplay?.slice(0, 4) || '🌐';

    return (
      <Dropdown
        overlay={
          <div
            style={{
              background: 'white',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid #d9d9d9',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {createDropdownItems.map(item => {
              if (item.type === 'group') {
                return (
                  <div key={item.key}>
                    <div
                      style={{
                        padding: '8px 12px',
                        fontSize: '12px',
                        color: '#999',
                        fontWeight: 'bold',
                      }}
                    >
                      {item.label}
                    </div>
                    {item.children.map(child => (
                      <div
                        key={child.key}
                        onClick={child.onClick}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                        onMouseEnter={e => {
                          (e.target as HTMLElement).style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={e => {
                          (e.target as HTMLElement).style.backgroundColor = 'transparent';
                        }}
                      >
                        {child.label}
                      </div>
                    ))}
                  </div>
                );
              } else {
                return (
                  <div
                    key={item.key}
                    onClick={item.onClick}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                    onMouseEnter={e => {
                      (e.target as HTMLElement).style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={e => {
                      (e.target as HTMLElement).style.backgroundColor = 'transparent';
                    }}
                  >
                    {item.label}
                  </div>
                );
              }
            })}
          </div>
        }
        placement="bottomRight"
        trigger={['click']}
        disabled={!currentLanguage || isMutating || isFetching}
        onVisibleChange={setIsDropdownOpen}
        getPopupContainer={trigger => trigger.parentElement || document.body}
      >
        <MobileSelector>
          <span className="flag">{flag}</span>
          <DownOutlined className={`chevron ${isDropdownOpen ? 'rotated' : ''}`} />
        </MobileSelector>
      </Dropdown>
    );
  }

  // If hideMobileVersion is true or we're on desktop, show the desktop version

  return (
    <div>
      <Select
        {...selectProps}
        options={groupedOptions}
        onSelect={(option: string) => {
          onChange(option);
        }}
        loading={!currentLanguage || isMutating || isFetching}
        value={currentLanguageDisplay}
      />
    </div>
  );
};
